import {
	type AIGenerateRequest,
	type AIMode,
	type AISettingsPublic,
	getHostBridge,
	isEmbedded,
} from "@emkit/adapter-emdash/bridge";
import type { Page, Widget } from "@emkit/builder-core";
import { useEffect, useState } from "react";
import { useEditor, useSelectedWidgetId } from "../store.js";

/**
 * Bottom-anchored prompt box that runs a generation through the host's AI
 * provider and applies the result to the page.
 *
 * Mode is auto-derived from the current selection:
 *  - widget selected   → "selection" (rewrite that widget in place)
 *  - nothing selected  → "page" by default, switchable to "widget" via the
 *    Add-new toggle (so a single click can append rather than replace).
 *
 * Hidden in standalone mode and while no provider is configured (the
 * Toolbar's ⚡ AI button is the entry point in those states).
 */
export function AIPromptBox() {
	const embedded = isEmbedded();
	const page = useEditor((s) => s.page);
	const setPage = useEditor((s) => s.setPage);
	const appendWidget = useEditor((s) => s.appendWidget);
	const replaceWidget = useEditor((s) => s.replaceWidget);
	const selectedWidgetId = useSelectedWidgetId();
	const openAISettings = useEditor((s) => s.openAISettings);

	const [settings, setSettings] = useState<AISettingsPublic | null>(null);
	const [prompt, setPrompt] = useState("");
	// "addNew" toggle is only meaningful when nothing is selected — it picks
	// between page-level edits ("make the hero darker") and append-a-widget
	// ("add a pricing table"). When something is selected the mode is
	// always "selection".
	const [addNew, setAddNew] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lastUsage, setLastUsage] = useState<string | null>(null);

	const isAISettingsOpen = useEditor((s) => s.isAISettingsOpen);

	// Refresh settings on three triggers: mount, window focus, and the AI
	// Settings modal closing. Single effect so we don't double-fire.
	useEffect(() => {
		if (!embedded || isAISettingsOpen) return;
		let cancelled = false;
		const refresh = () => {
			getHostBridge()
				.getAISettings()
				.then((s) => {
					if (!cancelled) setSettings(s);
				})
				.catch(() => {
					/* settings not yet available */
				});
		};
		refresh();
		const onFocus = () => refresh();
		window.addEventListener("focus", onFocus);
		return () => {
			cancelled = true;
			window.removeEventListener("focus", onFocus);
		};
	}, [embedded, isAISettingsOpen]);

	if (!embedded) return null;

	const configured = !!settings?.hasKey;
	const mode: AIMode = selectedWidgetId
		? "selection"
		: addNew
			? "widget"
			: "page";

	const submit = async () => {
		if (!configured || !prompt.trim()) return;
		setBusy(true);
		setError(null);
		setLastUsage(null);
		try {
			const selectedWidget = selectedWidgetId
				? findWidget(page, selectedWidgetId)
				: undefined;
			const payload: AIGenerateRequest = {
				mode,
				prompt: prompt.trim(),
				page,
				selectedWidget,
			};
			const result = await getHostBridge().generateWithAI(payload);

			if (result.mode === "page" && result.page) {
				setPage(result.page);
			} else if (result.mode === "widget" && result.widget) {
				appendWidget(result.widget as Widget);
			} else if (result.mode === "selection" && result.widget && selectedWidgetId) {
				replaceWidget(selectedWidgetId, result.widget as Widget);
			} else {
				setError("AI returned an unexpected response shape");
				return;
			}
			setPrompt("");
			if (result.usage) {
				const tokens = (result.usage.inputTokens ?? 0) + (result.usage.outputTokens ?? 0);
				if (tokens > 0) setLastUsage(`${tokens.toLocaleString()} tokens`);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "Generation failed");
		} finally {
			setBusy(false);
		}
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Cmd/Ctrl+Enter to submit; plain Enter inserts a newline so users
		// can write multi-line prompts.
		if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
			e.preventDefault();
			void submit();
		}
	};

	const placeholder = configured
		? mode === "selection"
			? "Describe how to change this widget — Cmd+Enter to send"
			: mode === "widget"
				? "Describe a widget to add — Cmd+Enter to send"
				: "Describe how to change this page — Cmd+Enter to send"
		: "Configure an AI provider in ⚡ AI to start generating";

	return (
		<div className="emke-ai-promptbox">
			<div className="emke-ai-promptbox-header">
				<span className="emke-ai-promptbox-title">
					⚡ AI · <span style={{ color: "var(--fg-muted)", fontWeight: 400 }}>{modeLabel(mode, !!selectedWidgetId)}</span>
				</span>
				{!selectedWidgetId && configured && (
					<label className="emke-ai-promptbox-toggle">
						<input
							type="checkbox"
							checked={addNew}
							onChange={(e) => setAddNew(e.target.checked)}
						/>
						Add new widget instead of editing the page
					</label>
				)}
				{!configured && (
					<button
						type="button"
						className="emke-btn"
						onClick={openAISettings}
						style={{ marginLeft: "auto", fontSize: 11, padding: "2px 8px" }}
					>
						Configure
					</button>
				)}
				{lastUsage && (
					<span style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-dim)" }}>
						{lastUsage}
					</span>
				)}
			</div>

			{error && <div className="emke-ai-promptbox-error">{error}</div>}

			<div className="emke-ai-promptbox-input">
				<textarea
					value={prompt}
					onChange={(e) => setPrompt(e.target.value)}
					onKeyDown={onKeyDown}
					placeholder={placeholder}
					disabled={!configured || busy}
					rows={2}
				/>
				<button
					type="button"
					className="emke-btn is-primary"
					onClick={() => void submit()}
					disabled={!configured || busy || !prompt.trim()}
				>
					{busy ? "Generating…" : "Generate"}
				</button>
			</div>
		</div>
	);
}

function modeLabel(mode: AIMode, hasSelection: boolean): string {
	if (mode === "selection") return "rewriting selected widget";
	if (mode === "widget") return "adding a new widget";
	return hasSelection ? "editing page" : "editing page";
}

function findWidget(page: Page, id: string): Widget | undefined {
	for (const section of page.sections) {
		for (const column of section.columns) {
			for (const widget of column.widgets) {
				if (widget.id === id) return widget;
			}
		}
	}
	return undefined;
}
